import React, { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { FaPaperclip } from "react-icons/fa";
import {
  getOpeningBalances,
  clearError,
} from "../../../../redux/Slices/authSlice";


// opening balance details
const OpeningBalanceDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const { openingBalances, isLoading, error } = useSelector(
    (state) => state.auth
  );
  const lastErrorRef = useRef({ message: null, time: 0 });

  useEffect(() => {
    dispatch(getOpeningBalances({ page: 1, per_page: 10 }));
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, { rtl: true });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 3000);
    }
  }, [error, dispatch]);

  const parseDetails = (details) => {
    if (!details) return [];
    if (Array.isArray(details)) return details;
    if (typeof details === "string") {
      try {
        const parsed = JSON.parse(details);
        return Array.isArray(parsed) ? parsed : [];
      } catch (err) {
        return [];
      }
    }
    if (typeof details === "object") {
      return Object.values(details);
    }
    return [];
  };

  const parseAttachments = (attachments) => {
    if (!attachments) return [];
    if (Array.isArray(attachments)) return attachments;
    if (typeof attachments === "object") {
      return Object.values(attachments);
    }
    return [];
  };

  const normalizedList = useMemo(() => {
    const source = openingBalances;

    const normalizeRecord = (record) => {
      if (!record) return null;
      const entry = record.entry || record.data || record;
      if (!entry) return null;
      const details = parseDetails(
        entry.details || record.details || record.data?.details
      );
      const attachments = parseAttachments(
        entry.attachments || record.attachments || record.data?.attachments
      );

      return {
        id: entry.id ?? record.id,
        entry_date: entry.entry_date || entry.date || record.entry_date || null,
        date: entry.date || entry.entry_date || record.date || null,
        description:
          entry.description ||
          record.description ||
          record.reference ||
          "",
        status: entry.status || record.status || null,
        summary:
          entry.summary || record.summary || record.data?.summary || null,
        total_debit:
          entry.total_debit ||
          record.total_debit ||
          entry.summary?.total_debit ||
          0,
        total_credit:
          entry.total_credit ||
          record.total_credit ||
          entry.summary?.total_credit ||
          0,
        details,
        attachments,
        raw: record,
      };
    };

    if (Array.isArray(source)) {
      return source.map(normalizeRecord).filter(Boolean);
    }
    if (Array.isArray(source?.data?.data)) {
      return source.data.data.map(normalizeRecord).filter(Boolean);
    }
    if (Array.isArray(source?.data)) {
      return source.data.map(normalizeRecord).filter(Boolean);
    }
    if (Array.isArray(source?.items)) {
      return source.items.map(normalizeRecord).filter(Boolean);
    }
    if (source?.data?.entry) {
      const record = normalizeRecord(source.data.entry);
      return record ? [record] : [];
    }
    if (
      source &&
      typeof source === "object" &&
      !Array.isArray(source) &&
      (source.id !== undefined ||
        source.entry_date ||
        source.details ||
        source.total_debit !== undefined ||
        source.total_credit !== undefined)
    ) {
      const record = normalizeRecord(source);
      return record ? [record] : [];
    }
    return [];
  }, [openingBalances]);

  const parsedData = useMemo(() => {
    if (!id) return null;
    return normalizedList.find(
      (item) => String(item.id) === String(id)
    );
  }, [normalizedList, id]);

  if (isLoading || !parsedData) {
    return (
      <div
        style={{
          padding: "30px",
          backgroundColor: "#14263b",
          minHeight: "calc(100vh - 80px)",
          color: "white",
        }}
      >
        جاري التحميل...
      </div>
    );
  }

  const attachments = Array.isArray(parsedData.attachments)
    ? parsedData.attachments
    : [];
  const details = Array.isArray(parsedData.details)
    ? parsedData.details
    : [];

  const totalDebit =
    parsedData.total_debit ??
    parsedData.summary?.total_debit ??
    details.reduce((sum, detail) => sum + (parseFloat(detail.debit) || 0), 0);

  const totalCredit =
    parsedData.total_credit ??
    parsedData.summary?.total_credit ??
    details.reduce((sum, detail) => sum + (parseFloat(detail.credit) || 0), 0);

  const difference = Number(totalDebit) - Number(totalCredit);

  const formatCurrency = (value) =>
    Number(value || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const attachmentDisplay = (attachment) => {
    const name =
      attachment.original_name ||
      attachment.file_name ||
      attachment.name ||
      attachment.filename ||
      `مرفق-${attachment.id ?? ""}`;
    const url =
      attachment.url ||
      attachment.file_url ||
      attachment.path ||
      attachment.media_url ||
      attachment.full_url ||
      attachment.download_url ||
      "";
    const size =
      attachment.size != null
        ? (Number(attachment.size) / 1024).toFixed(2)
        : null;
    return { name, url, size };
  };

  const detailHeaders = [
    { label: "#", width: "60px" },
    { label: "الحساب", width: "200px" },
    { label: "الكود", width: "120px" },
    { label: "الوصف", width: "220px" },
    { label: "مدين", width: "150px" },
    { label: "دائن", width: "150px" },
  ];

  return (
    <div
      style={{
        padding: "30px",
        backgroundColor: "#14263b",
        minHeight: "calc(100vh - 80px)",
        color: "white",
      }}
    >
      <button
        onClick={() => navigate("/opening-balance")}
        style={{
          background: "#666",
          color: "white",
          border: "none",
          borderRadius: 8,
          padding: "8px 12px",
          cursor: "pointer",
          marginBottom: "16px",
        }}
      >
        رجوع
      </button>
      <div
        style={{
          backgroundColor: "#1b3350",
          border: "1px solid #333",
          borderRadius: "12px",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: "16px",
            borderBottom: "1px solid #333",
            backgroundColor: "#1a1f2e",
          }}
        >
          <strong>تفاصيل الرصيد الافتتاحي #{parsedData.id}</strong>
        </div>
        <div style={{ padding: "20px" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: "16px",
              marginBottom: "24px",
            }}
          >
            <DetailCard label="التاريخ" value={parsedData.date || "-"} />
            <DetailCard label="الحالة" value={parsedData.status || "-"} />
            <DetailCard label="إجمالي المدين" value={formatCurrency(totalDebit)} />
            <DetailCard
              label="إجمالي الدائن"
              value={formatCurrency(totalCredit)}
            />
            <DetailCard
              label="الفرق"
              value={formatCurrency(difference)}
              valueColor={
                Math.abs(difference) < 0.001 ? "#22c55e" : "#dc2626"
              }
            />
          </div>

          {(parsedData.description || "").trim() !== "" && (
            <div style={{ marginBottom: "24px" }}>
              <h3
                style={{
                  fontSize: "16px",
                  marginBottom: "8px",
                  fontWeight: "bold",
                }}
              >
                الوصف
              </h3>
              <div style={{ color: "#e5e7eb", lineHeight: 1.6 }}>
                {parsedData.description}
              </div>
            </div>
          )}

          {attachments.length > 0 && (
            <div style={{ marginBottom: "32px" }}>
              <h3
                style={{
                  fontSize: "16px",
                  marginBottom: "12px",
                  fontWeight: "bold",
                }}
              >
                المرفقات
              </h3>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    "repeat(auto-fill, minmax(min(220px, 100%), 1fr))",
                  gap: "16px",
                }}
              >
                {attachments.map((attachment) => {
                  const { name, url, size } = attachmentDisplay(attachment);
                  return (
                    <a
                      key={`attachment-${attachment.id || name}`}
                      href={url || "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        backgroundColor: "#1a1f2e",
                        padding: "16px",
                        borderRadius: "10px",
                        border: "1px solid #333",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: "10px",
                        color: url ? "white" : "#888",
                        textDecoration: "none",
                        cursor: url ? "pointer" : "default",
                        transition: "transform 0.2s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = url ? "translateY(-4px)" : "none";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "none";
                      }}
                    >
                      <FaPaperclip style={{ fontSize: "24px", color: "#AC2000" }} />
                      <span
                        style={{
                          fontSize: "13px",
                          textAlign: "center",
                          wordBreak: "break-word",
                        }}
                      >
                        {name}
                      </span>
                      {size && (
                        <span style={{ fontSize: "11px", color: "#999" }}>
                          {size} KB
                        </span>
                      )}
                    </a>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <h3
              style={{ fontSize: "18px", marginBottom: "16px", fontWeight: "bold" }}
            >
              تفاصيل السطور
            </h3>
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "separate",
                  borderSpacing: 0,
                }}
              >
                <thead>
                  <tr style={{ backgroundColor: "#1a1f2e" }}>
                    {detailHeaders.map((header) => (
                      <th
                        key={header.label}
                        style={{
                          padding: "18px 16px",
                          color: "white",
                          borderBottom: "2px solid #333",
                          fontSize: "14px",
                          fontWeight: "bold",
                          textAlign: "center",
                          width: header.width,
                          minWidth: header.width,
                        }}
                      >
                        {header.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {details.length === 0 ? (
                    <tr>
                      <td
                        colSpan={detailHeaders.length}
                        style={{
                          padding: "32px 16px",
                          textAlign: "center",
                          color: "#9ca3af",
                        }}
                      >
                        لا توجد تفاصيل متاحة
                      </td>
                    </tr>
                  ) : (
                    details.map((detail, index) => {
                      const accountName =
                        detail.account_name ||
                        detail.account?.name_ar ||
                        detail.account?.name ||
                        "-";
                      const accountCode =
                        detail.account_code ||
                        detail.account?.code ||
                        detail.code ||
                        "-";
                      return (
                        <tr
                          key={`detail-${detail.id || index}`}
                          style={{
                            borderBottom: "1px solid #333",
                            backgroundColor:
                              index % 2 === 0 ? "#1b3350" : "#1a1f2e",
                            transition: "background-color 0.2s",
                          }}
                          onMouseEnter={(ev) => {
                            ev.currentTarget.style.backgroundColor = "#2a3441";
                          }}
                          onMouseLeave={(ev) => {
                            ev.currentTarget.style.backgroundColor =
                              index % 2 === 0 ? "#1b3350" : "#1a1f2e";
                          }}
                        >
                          <td style={tableCellStyle}>{index + 1}</td>
                          <td style={tableCellStyle}>{accountName}</td>
                          <td style={tableCellStyle}>{accountCode}</td>
                          <td style={tableCellStyle}>
                            {detail.line_description ||
                              detail.description ||
                              detail.note ||
                              "-"}
                          </td>
                          <td
                            style={{
                              ...tableCellStyle,
                              fontWeight: 600,
                              color: "#facc15",
                            }}
                          >
                            {formatCurrency(detail.debit)}
                          </td>
                          <td
                            style={{
                              ...tableCellStyle,
                              fontWeight: 600,
                              color: "#60a5fa",
                            }}
                          >
                            {formatCurrency(detail.credit)}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const DetailCard = ({ label, value, valueColor = "white" }) => (
  <div
    style={{
      backgroundColor: "#1a1f2e",
      borderRadius: "10px",
      border: "1px solid #333",
      padding: "16px",
    }}
  >
    <div style={{ color: "#9ca3af", fontSize: 12, marginBottom: 6 }}>{label}</div>
    <div style={{ fontSize: "16px", fontWeight: "bold", color: valueColor }}>
      {value ?? "-"}
    </div>
  </div>
);

const tableCellStyle = {
  padding: "18px 16px",
  textAlign: "center",
  color: "white",
  fontSize: "14px",
};

export default OpeningBalanceDetails;

